// A flapping direct path, and riding out a blip (Josh 10-09: the phone over cellular, srflx↔srflx direct, dropped
// every one to two minutes; each drop was a fresh connection). Two rules, this browser's half — the Mac's peer.html
// restarts ICE (it is the offerer) when asked, or by itself when its side goes "disconnected":
//   · ICE "disconnected" is often a blip: the connection is not torn down for it. The browser asks the Mac for an ICE
//     restart over the data channel (`{rtc: {t: 'relay', why: 'ice-disconnected'}}` — the Mac brings its relay in, as
//     its direct-first timeout does) and the heartbeat holds off for RECOVER_MS. "connected" again → nothing lost;
//     "failed", or still down when the grace is over → the usual teardown and reconnect.
//   · A direct path (host or NAT) that drops twice within FLAP_WINDOW_MS: this Mac prefers the relay for PREFER_MS
//     (kept per Mac in this browser). Its next connections still start the usual way — direct first — and ask for the
//     relay right after the answer instead of after the Mac's 4.5 s window, and take the restart relay-only.
//
// Seams: `watchIce(transport, {now, onStatus})` → stop; `noteDrop(room, {route, troubled}, now)` → true when the
// rule now prefers the relay; `prefersRelay(room, now)`.

export const RECOVER_MS = 20000
export const FLAP_WINDOW_MS = 3 * 60000
export const PREFER_MS = 3600000
const KEY = 'cody.flakyDirect'

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {} } catch { return {} } }
const write = (all) => { try { if (Object.keys(all).length) localStorage.setItem(KEY, JSON.stringify(all)); else localStorage.removeItem(KEY) } catch {} }

/// Is the relay preferred for this Mac now (two direct drops within three minutes, in the last hour)?
export function prefersRelay(room, now = Date.now()) {
  const e = room ? read()[room] : null
  return !!e && (e.until || 0) > now
}

/// A connection to `room` ended. Counted only when it went direct and was troubled (ICE disconnected or failed, or the
/// heartbeat gave up) — not a pause, a forget or the page closing. → true when this drop turned the preference on.
export function noteDrop(room, { route, troubled } = {}, now = Date.now()) {
  if (!room || !troubled || !route || route.kind === 'relay') return false
  const all = read()
  const e = all[room] || { drops: [], until: 0 }
  e.drops = [...(e.drops || []).filter((t) => now - t < FLAP_WINDOW_MS), now]
  const turned = e.drops.length >= 2 && !((e.until || 0) > now)
  if (e.drops.length >= 2) e.until = now + PREFER_MS
  all[room] = e
  for (const [k, v] of Object.entries(all)) if (k !== room && (v.until || 0) <= now && !(v.drops || []).some((t) => now - t < FLAP_WINDOW_MS)) delete all[k]
  write(all)
  return turned
}

/// Rides out ICE "disconnected": asks the Mac for a restart and holds the heartbeat off (`transport.recovering`, a
/// deadline the channel's heartbeat reads) instead of tearing the connection down. Marks `transport.troubled`.
export function watchIce(transport, { now = () => Date.now(), onStatus = () => {} } = {}) {
  const pc = transport?.pc
  if (!pc?.addEventListener) return () => {}
  const ask = () => {
    const ch = transport.channel
    if (ch?.readyState === 'open') { try { ch.send(JSON.stringify({ rtc: { t: 'relay', why: 'ice-disconnected' } })) } catch {} }
  }
  const on = () => {
    const s = pc.iceConnectionState
    if (s === 'disconnected') {
      transport.troubled = true
      if ((transport.recovering || 0) > now()) return
      transport.recovering = now() + RECOVER_MS
      transport.restartsAsked = (transport.restartsAsked || 0) + 1
      onStatus('The link to the Mac wavered — asking for an ICE restart…')
      ask()
    } else if (s === 'connected' || s === 'completed') {
      if (transport.recovering) onStatus('The link to the Mac came back.')
      transport.recovering = 0
      transport.missed = 0
    } else if (s === 'failed') {
      transport.troubled = true
      transport.recovering = 0
      // ICE failed with the link up: the data channel may never say "closed" on its own, which left the Mac loop
      // (macs.js run) waiting forever — the phone had to be closed and reopened for every reconnect (Josh 10-14)
      onStatus('The link to the Mac failed — reconnecting…')
      try { transport.close?.() } catch {}
    }
  }
  pc.addEventListener('iceconnectionstatechange', on)
  return () => pc.removeEventListener('iceconnectionstatechange', on)
}
